import React from "react";
import { motion } from "framer-motion";
import { useT } from "@/lib/i18n";
import { Check, X } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

export default function Comparison() {
    const { t } = useT();
    const rows = t("comparison.rows") || [];
    return (
        <section
            data-testid="sn-comparison"
            className="relative bg-sn-black text-sn-silver py-24 md:py-40 overflow-hidden"
        >
            <div className="absolute inset-0 hero-radial opacity-60" />
            <div className="grain" />
            <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.9, ease: EASE }}
                    className="flex items-baseline gap-4 mb-14 md:mb-20"
                >
                    <span className="font-mono-e text-[11px] uppercase tracking-[0.3em] text-sn-blue">
                        {t("comparison.eyebrow")}
                    </span>
                    <span className="hairline-dark flex-1" />
                </motion.div>

                <motion.h2
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.9, ease: EASE }}
                    className="font-serif text-5xl md:text-7xl leading-[0.98] tracking-tight max-w-3xl"
                >
                    {t("comparison.headline_a")}{" "}
                    <em className="italic text-sn-green">{t("comparison.headline_b")}</em>.
                </motion.h2>

                <div className="mt-16 border-t border-white/10">
                    {/* Header row */}
                    <div className="grid grid-cols-12 gap-4 py-5 border-b border-white/10 font-mono-e text-[10px] md:text-[11px] uppercase tracking-[0.2em]">
                        <div className="col-span-4 md:col-span-4 text-sn-fog" />
                        <div className="col-span-4 text-sn-fog">
                            {t("comparison.colA")}
                        </div>
                        <div className="col-span-4 text-sn-green flex items-center gap-2">
                            {t("comparison.colB")}
                        </div>
                    </div>

                    {rows.map((r, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.4 }}
                            transition={{ duration: 0.6, delay: i * 0.05, ease: EASE }}
                            className="grid grid-cols-12 gap-4 py-6 border-b border-white/5 items-start"
                        >
                            <div className="col-span-4 font-mono-e text-[10px] md:text-xs uppercase tracking-[0.18em] text-sn-silver/80 pt-1">
                                {r.label}
                            </div>
                            <div className="col-span-4 flex items-start gap-2 text-sn-fog text-sm md:text-base">
                                <X size={15} className="text-sn-fog/60 mt-1 shrink-0" />
                                <span>{r.a}</span>
                            </div>
                            <div className="col-span-4 flex items-start gap-2 text-sn-silver text-sm md:text-base">
                                <Check size={15} className="text-sn-green mt-1 shrink-0" />
                                <span>{r.b}</span>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
